/**
 * Secure redirect path validation utilities
 * Following LinkedIn's approach - only internal paths allowed
 */
import { Route } from 'next';

/**
 * Default redirect path when no valid returnTo is provided
 */
export const DEFAULT_REDIRECT_PATH = '/';

/**
 * Validates if a redirect path is safe and allowed
 * @param path - The path to validate (should not include domain)
 * @returns boolean indicating if the path is valid
 */
export function isValidRedirectPath(path: string): boolean {
  if (typeof path !== 'string' || !path) {
    return false;
  }

  if (!path.startsWith('/')) {
    return false;
  }

  if (path.includes('://') || path.startsWith('//')) {
    return false;
  }

  if (path.includes('<') || path.includes('>') || path.includes('"')) {
    return false;
  }

  return true;
}

/**
 * Safely gets a validated redirect path from a URL parameter
 * @param returnTo - The returnTo parameter value
 * @returns A safe redirect path or the default path
 */
export function getSafeRedirectPath(returnTo: string | null): Route {
  if (!returnTo) {
    return DEFAULT_REDIRECT_PATH;
  }

  try {
    const decodedPath = decodeURIComponent(returnTo);

    if (isValidRedirectPath(decodedPath)) {
      return decodedPath as Route;
    }
  } catch (_) {
    console.warn('Invalid returnTo parameter encoding:', returnTo);
  }

  return DEFAULT_REDIRECT_PATH;
}

/**
 * Creates a safe returnTo parameter for URLs
 * @param path - The path to encode as returnTo parameter
 * @returns Encoded returnTo parameter or null if path is invalid
 */
export function createReturnToParameter(path: string): string | null {
  if (!isValidRedirectPath(path)) {
    return null;
  }

  return encodeURIComponent(path);
}

/**
 * Builds a URL with returnTo parameter preserved
 * @param basePath - The base path (e.g., '/login', '/signup')
 * @param returnTo - The returnTo parameter value (can be from searchParams.get('returnTo'))
 * @returns Complete URL with returnTo parameter if valid, or just the base path
 *
 * @example
 * buildUrlWithReturnTo('/login', '%2Finvitation%2Fabc123')
 * // Returns: '/login?returnTo=%2Finvitation%2Fabc123'
 *
 * buildUrlWithReturnTo('/signup', null)
 * // Returns: '/signup'
 */
export function buildUrlWithReturnTo(basePath: string, returnTo: string | null): Route {
  if (!returnTo) {
    return basePath as Route;
  }

  try {
    const decodedPath = decodeURIComponent(returnTo);
    if (isValidRedirectPath(decodedPath)) {
      // Always re-encode from the decoded form: searchParams.get() hands back a
      // decoded value, and embedding it raw would let any `&` in the path's query
      // split the rest of the returnTo into separate top-level params.
      return `${basePath}?returnTo=${encodeURIComponent(decodedPath)}` as Route;
    }
  } catch (_) {
    console.warn('Invalid returnTo parameter encoding:', returnTo);
  }

  return basePath as Route;
}

/**
 * Appends an `email` query param to an existing URL, preserving any params already on it.
 * Used to keep the email pre-fill flowing as the user moves between the auth pages.
 * @param url - A path, optionally already carrying query params (e.g. from buildUrlWithReturnTo)
 * @param email - The email to forward; when null/empty the url is returned unchanged
 */
export function withEmailParam(url: string, email: string | null): Route {
  if (!email) {
    return url as Route;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}email=${encodeURIComponent(email)}` as Route;
}
